import { ExternalLink, Info, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useScrollOverflow } from "@/hooks/useScrollOverflow";
import { ScrollHintFade } from "@/components/ScrollHintFade";

export const FOUNDER_IMG =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_wearspray-refresh/artifacts/5xrgjpf0_greg%20pic%20smiling%20July%202026%20.jpg";

// Rotating header accent so each slide's number + label pop with intentional
// variety (cyan / magenta / gold) instead of one flat muted tone throughout.
export const HEADER_COLORS = ["#00e5ff", "#ff2d78", "#e6c068"];

export function SlideHeader({ n, label }) {
  const color = HEADER_COLORS[(parseInt(n, 10) - 1) % HEADER_COLORS.length];
  return (
    <div className="flex items-center gap-4 mb-4 md:mb-6" data-testid={`pitch-slide-header-${n}`}>
      <span className="font-mono text-2xl md:text-3xl font-light tracking-tight" style={{ color }}>
        {n}
      </span>
      <span className="h-px w-10" style={{ backgroundColor: `${color}55` }} />
      <span className="font-mono text-sm md:text-base tracking-[0.2em] uppercase font-medium" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

export function DeepDive({ testid, label = "Go Deeper", title, children }) {
  const { ref, hasMore, onScroll } = useScrollOverflow();
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          data-testid={`pitch-deepdive-trigger-${testid}`}
          className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-accent border border-accent/40 rounded-full px-4 py-2.5 hover:bg-accent/10 transition-colors"
        >
          <Info size={13} />
          {label}
        </button>
      </DialogTrigger>
      <DialogContent
        data-testid={`pitch-deepdive-modal-${testid}`}
        className="bg-[#0d0d0d] border border-zinc-800 text-white max-w-lg max-h-[85vh] rounded-xl p-0 flex flex-col overflow-hidden"
      >
        <div ref={ref} onScroll={onScroll} data-lenis-prevent className="overflow-y-auto p-6">
          <DialogTitle className="font-heading text-2xl font-light tracking-tight text-white pr-6">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
          <div className="mt-2">{children}</div>
        </div>
        <ScrollHintFade show={hasMore} testid={`pitch-deepdive-scroll-hint-${testid}`} fadeColor="#0d0d0d" />
      </DialogContent>
    </Dialog>
  );
}

export function SiteLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 font-body text-sm text-accent hover:underline mt-4"
    >
      {children}
      <ExternalLink size={13} />
    </a>
  );
}

export function Headline({ children }) {
  return (
    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-light tracking-tighter leading-[1.08] text-white max-w-3xl">
      {children}
    </h2>
  );
}

export function Body({ children }) {
  return (
    <p className="font-body text-zinc-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
      {children}
    </p>
  );
}

export function StepList({ steps, testid }) {
  return (
    <div className="space-y-2 mt-6 max-w-2xl" data-testid={testid}>
      {steps.map((s, i) => (
        <div key={s.title} className="flex gap-4" data-testid={`${testid}-step-${i}`}>
          <span className="font-mono text-xs w-6 h-6 shrink-0 rounded-full border border-accent/40 text-accent flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="font-heading text-white text-base font-medium">{s.title}</span>
              {s.badge && (
                <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-black bg-accent rounded-full px-2.5 py-0.5">
                  {s.badge}
                </span>
              )}
            </div>
            <p className="font-body text-zinc-400 text-sm leading-relaxed mt-1">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function InfoBoxGrid({ boxes, testid }) {
  return (
    <div className="grid sm:grid-cols-3 gap-4 mt-6" data-testid={testid}>
      {boxes.map((b) => (
        <div key={b.title} className="border border-zinc-800 rounded-xl p-5 bg-white/[0.02]" data-testid={`${testid}-box-${b.title.toLowerCase().replace(/\s+/g, "-")}`}>
          {b.icon && (
            <div className="w-9 h-9 rounded-lg border border-zinc-700 flex items-center justify-center text-accent mb-3">
              <b.icon size={16} strokeWidth={1.7} />
            </div>
          )}
          <p className="font-heading text-white text-base font-medium">{b.title}</p>
          <p className="font-body text-zinc-400 text-sm leading-relaxed mt-2">{b.body}</p>
          {b.extra && <p className="font-mono text-[11px] tracking-[0.08em] text-zinc-500 mt-3">{b.extra}</p>}
        </div>
      ))}
    </div>
  );
}

export function StatRow({ stats, testid }) {
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-6 mt-7" data-testid={testid}>
      {stats.map((s) => (
        <div key={s.label}>
          <p className="font-heading text-4xl md:text-5xl font-light tracking-tight" style={{ color: s.color }}>
            {s.value}
          </p>
          <p className="font-body text-zinc-500 text-sm leading-snug mt-2 max-w-[10rem]">{s.label}</p>
        </div>
      ))}
    </div>
  );
}

export function CheckOrX({ ok }) {
  return ok ? (
    <span className="w-14 sm:w-16 flex items-center justify-center text-accent shrink-0"><Check size={16} /></span>
  ) : (
    <span className="w-14 sm:w-16 flex items-center justify-center text-zinc-600 shrink-0"><X size={14} /></span>
  );
}
